import { TCCredentialKind, TCCredentialStatus } from 'src/api/generated';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';

/**
 * Default page size used by the credentials settings page.
 */
export const DEFAULT_CREDENTIALS_PAGE_SIZE = 20;

/**
 * Sentinel option value for selecting a custom provider.
 */
export const OTHER_CREDENTIAL_PROVIDER_OPTION = 'other' as const;

/**
 * A provider option shown in the credentials UI.
 */
export interface CredentialProviderOption {
  /** Provider identifier (submitted to the backend). */
  id: string;
  /** i18n label key for displaying the provider. */
  labelKey: TranslationKey;
}

/**
 * Providers supported by the product UI (v1).
 *
 * @remarks
 * This list is intended to grow as integrations ship. The backend still accepts any string provider id.
 */
export const CREDENTIAL_PROVIDER_OPTIONS: CredentialProviderOption[] = [
  { id: 'prop360', labelKey: 'settings.credentials.provider.prop360' },
  { id: 'kutt', labelKey: 'settings.credentials.provider.kutt' },
  { id: '2clics', labelKey: 'settings.credentials.provider.2clics' },
  {
    id: 'toppropiedades',
    labelKey: 'settings.credentials.provider.toppropiedades',
  },
  { id: 'kiteprop', labelKey: 'settings.credentials.provider.kiteprop' },
  { id: 'wasi', labelKey: 'settings.credentials.provider.wasi' },
  {
    id: 'mercadolibre',
    labelKey: 'settings.credentials.provider.mercadolibre',
  },
];

/**
 * Providers that enforce a specific credential kind.
 *
 * @remarks
 * This is a UX guardrail only; the backend still accepts any `kind` for any provider.
 */
export const CREDENTIAL_PROVIDER_FORCED_KIND: Partial<
  Record<string, TCCredentialKind>
> = {
  prop360: TCCredentialKind.BASIC,
  kutt: TCCredentialKind.BASIC,
  '2clics': TCCredentialKind.API_KEY,
  toppropiedades: TCCredentialKind.BEARER,
  kiteprop: TCCredentialKind.API_KEY,
  wasi: TCCredentialKind.API_KEY,
};

export const CREDENTIAL_KIND_LABEL_KEY_BY_KIND: Record<
  TCCredentialKind,
  TranslationKey
> = {
  [TCCredentialKind.API_KEY]: 'settings.credentials.kind.apiKey',
  [TCCredentialKind.BEARER]: 'settings.credentials.kind.bearer',
  [TCCredentialKind.BASIC]: 'settings.credentials.kind.basic',
  [TCCredentialKind.CUSTOM]: 'settings.credentials.kind.custom',
  [TCCredentialKind.OAUTH]: 'settings.credentials.kind.oauth',
};

/**
 * Returns the forced credential kind for a provider (if any).
 *
 * @param providerId - Provider identifier.
 * @returns Forced kind or undefined if the provider supports multiple kinds.
 */
export const getForcedCredentialKindForProvider = (
  providerId: string | undefined
): TCCredentialKind | undefined => {
  if (!providerId) return undefined;
  return CREDENTIAL_PROVIDER_FORCED_KIND[providerId];
};

/**
 * Filter value for credential kind.
 */
export type CredentialKindFilter = TCCredentialKind | 'all';

/**
 * Form state for creating a credential.
 */
export interface CredentialCreateFormState {
  /** Provider option selected from the supported list (or `other`). */
  providerOption: string;
  /** Provider identifier used when `providerOption` is `other`. */
  otherProvider: string;
  /** Human-friendly name for the credential. */
  name: string;
  /** Credential kind describing how it authenticates. */
  kind: TCCredentialKind;
  /** Whether this credential is active or disabled. */
  status: TCCredentialStatus;
  /** Whether this credential can be used by other users in the tenant. */
  sharedWithTenant: boolean;
  /** API key value (kind=apiKey). */
  apiKey: string;
  /** Bearer token value (kind=bearer). */
  bearerToken: string;
  /** Basic auth username (kind=basic). */
  basicUsername: string;
  /** Basic auth password (kind=basic). */
  basicPassword: string;
  /** JSON string representing a custom secret object (kind=custom). */
  customSecretJson: string;
}

/**
 * Creates default state for the create credential modal.
 */
export const createDefaultCredentialCreateFormState =
  (): CredentialCreateFormState => ({
    providerOption: '',
    otherProvider: '',
    name: '',
    kind: TCCredentialKind.API_KEY,
    status: TCCredentialStatus.ACTIVE,
    sharedWithTenant: false,
    apiKey: '',
    bearerToken: '',
    basicUsername: '',
    basicPassword: '',
    customSecretJson: '{}',
  });
